import React, { useState, useEffect, useRef } from 'react';
import './App.css';

interface Message {
  text: string;
  sender: 'user' | 'assistant';
}

const App: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [recognizing, setRecognizing] = useState<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const vapiInstanceRef = useRef<any>(null);
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  
  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'fr-FR';
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      
      recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
        setRecognizing(false);
        if (event.results.length > 0) {
          const transcript = event.results[0][0].transcript.trim();
          if (transcript) {
            sendUserMessage(transcript);
          }
        }
      };
      
      recognitionRef.current.onend = () => {
        setRecognizing(false);
      };
    }
  }, []);
  
  // Initialize Vapi
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/gh/VapiAI/html-script-tag@latest/dist/assets/index.js';
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);
    
    script.onload = () => {
      const apiKey = 'ed593bec-94f4-4cd0-af92-68e3d5a8b7eb';
      const assistant = '2cac35a3-c9ef-46e3-94d4-48348d7285e5';
      const buttonConfig = {};
      
      vapiInstanceRef.current = (window as any).vapiSDK.run({
        apiKey: apiKey,
        assistant: assistant,
        config: buttonConfig,
      });
      
      if (vapiInstanceRef.current && vapiInstanceRef.current.on) {
        vapiInstanceRef.current.on('message', (msg: any) => {
          if (
            msg.type === 'transcript' &&
            msg.transcriptType === 'final' &&
            msg.role === 'assistant' &&
            msg.id
          ) {
            handleAssistantMessage(msg.id, msg.transcript);
          }
        });
      }
    };
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  const handleAssistantMessage = debounce((messageId: string, transcript: string) => {
    if (!processedMessageIdsRef.current.has(messageId)) {
      processedMessageIdsRef.current.add(messageId);
      addMessage(transcript, 'assistant');
      
      if (isSpeaking) {
        window.speechSynthesis.cancel();
      }
      
      const utterance = new SpeechSynthesisUtterance(transcript);
      utterance.lang = 'fr-FR';
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  }, 1000);
  
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;
    
    return (...args: Parameters<T>) => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => func(...args), wait);
    };
  }
  
  const addMessage = (text: string, sender: 'user' | 'assistant') => {
    setMessages(prevMessages => [...prevMessages, { text, sender }]);
  };
  
  const sendUserMessage = (message: string) => {
    if (!message.trim()) return;
    
    addMessage(message, 'user');
    
    if (vapiInstanceRef.current && vapiInstanceRef.current.send) {
      try {
        vapiInstanceRef.current.send({
          type: 'add-message',
          message: { role: 'user', content: message }
        });
      } catch (error) {
        console.error('Erreur Vapi :', error);
      }
    }
  };
  
  const handleSendMessage = () => {
    sendUserMessage(inputText);
    setInputText('');
  };
  
  const handleMicClick = () => {
    if (!recognitionRef.current) return;
    
    if (!recognizing) {
      setRecognizing(true);
      recognitionRef.current.start();
    } else {
      setRecognizing(false);
      recognitionRef.current.stop();
    }
  };
  
  return (
    <div className="bg-gray-50 flex flex-col h-screen">
      <header className="flex items-center bg-blue-600 p-4 shadow">
        <img src="./alex.png" alt="Avatar" className="w-10 h-10 rounded-full mr-3" />
        <div>
          <p className="text-white font-bold">Alex</p>
          <p className="text-blue-200 text-xs">En ligne</p>
        </div>
      </header>

      <main 
        ref={chatContainerRef} 
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`flex w-full ${message.sender === 'assistant' ? 'justify-start' : 'justify-end'}`}
          >
            <div 
              className={`max-w-xs px-4 py-2 rounded-lg shadow mb-1 ${
                message.sender === 'assistant' 
                  ? 'bg-gray-200 text-gray-900' 
                  : 'bg-blue-500 text-white'
              }`}
            >
              {message.text}
            </div>
          </div>
        ))}
      </main>

      <footer className="bg-white p-4 border-t border-gray-200">
        <div className="flex items-center">
          <button 
            onClick={handleMicClick}
            className={`p-2 text-blue-500 hover:text-blue-700 focus:outline-none ${recognizing ? 'bg-blue-100' : ''}`} 
            title="Parler"
          >
            <i className="fas fa-microphone fa-lg"></i>
          </button>
          <input 
            type="text" 
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Écrivez votre message..." 
            className="flex-1 mx-3 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500" 
          />
          <button 
            onClick={handleSendMessage}
            className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none" 
            title="Envoyer"
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default App;